import React, { useEffect, useState } from "react";
import Select from "react-select";
import CustomDatePicker from "../components/CustomDatePicker";
import { apiGet } from "../Utils/apiFetch";
import { apiPath } from "../Utils/apiPath";
import moment from "moment";
import {
  betCheckData,
  betCheckDataInner,
  betCheckObj,
  DownlineHeader,
  formatDate,
  toFixedWithoutRounding,
} from "../Utils/Constant";
import Pagination from "../components/Pagination";
import ProfitLossCell from "../Utils/ProfitLossCell";
import ParlayBets from "./ParlayBets";
import ParlayModal from "./RiskManagement/ParlayModal";
import { startCase } from "lodash";
import { useContextSelector } from "use-context-selector";
import AuthContext from "../context/AuthContext";

/* ----------------------------- static config ----------------------------- */

const style = { padding: "8px" };
const styleBody = { padding: "8px 7px" };

const LIMIT_OPTIONS = [10, 25, 50, 100, 500, 1000];

const marketOptions = [
  { value: "all", label: "Select Market" },
  { value: "football", label: "Football" },
  { value: "cricket", label: "Cricket" },
  { value: "tennis", label: "Tennis" },
];

const currencyOptions = [{ value: "pbu", label: "USD" }];

const userOptions = [
  { value: "1", label: "User 1" },
  { value: "2", label: "User 2" },
  { value: "3", label: "User 3" },
];

const eventOptions = [
  { value: "1", label: "Event 1" },
  { value: "2", label: "Event 2" },
  { value: "3", label: "Event 3" },
];

// function (not a constant) so the dates are fresh every time it is called
const getDefaultFilter = () => ({
  type: "all",
  pageType: "",
  page: 1,
  limit: 10,
  sortBy: "BetSettledTime",
  fromPeriod: moment().subtract(1, "day").format("YYYY-MM-DD HH:mm:ss"),
  toPeriod: moment().format("YYYY-MM-DD HH:mm:ss"),
});

const DEFAULT_PAGINATION = {
  page: 1,
  limit: 10,
  totalRecords: 0,
  totalPages: 1,
  hasNextPage: false,
  hasPreviousPage: false,
};

// drop empty values so the API only receives filters that are actually set
const buildParams = (f) =>
  Object.fromEntries(
    Object.entries(f).filter(
      ([, v]) => v !== "" && v !== null && v !== undefined,
    ),
  );

/* ------------------------------- component ------------------------------- */

const BetList = () => {
  const user = useContextSelector(AuthContext, (ctx) => ctx.user);

  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [show, setShow] = useState({ status: false, data: [] });
  const [filter, setFilter] = useState(getDefaultFilter);
  // debounced copy of filter: this is what actually hits the API
  const [debouncedFilter, setDebouncedFilter] = useState(filter);
  const [refreshKey, setRefreshKey] = useState(0);

  const getData = async (activeFilter, config = {}) => {
    const { status, data: response } = await apiGet(
      apiPath.newLiveBet,
      buildParams(activeFilter),
      config,
    );
    if (status === 200 && response.success) {
      setData(response.data || []);
      setPagination(response.pagination || DEFAULT_PAGINATION);
    }
  };

  // debounce so typing in search / stake / IP doesn't fire a request per keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedFilter(filter), 500);
    return () => clearTimeout(t);
  }, [filter]);

  // fetch whenever ANY filter changes (type, betType, status, dates, page, limit, ...)
  useEffect(() => {
    getData(debouncedFilter);
  }, [debouncedFilter, refreshKey]);

  /* ------------------------------- handlers ------------------------------ */

  // every filter change resets to page 1
  const updateFilter = (patch) =>
    setFilter((prev) => ({ ...prev, page: 1, ...patch }));

  const handleTypeChange = (newType) => updateFilter({ type: newType });
  const handleBetTypeChange = (newBetType) =>
    updateFilter({ betType: newBetType });
  const handleStatusChange = (e) => updateFilter({ status: e.target.value });
  const handleLimitChange = (e) =>
    updateFilter({ limit: Number(e.target.value) });
  const handleSortChange = (e) => updateFilter({ sortBy: e.target.value });
  const handleSearchChange = (e) => updateFilter({ search: e.target.value });
  const handleIpChange = (e) => updateFilter({ ipAddress: e.target.value });
  const handleStakeChange = (e) => updateFilter({ stake: e.target.value });
  const handleUserChange = (s) => updateFilter({ userId: s?.value || "" });
  const handleEventChange = (s) => updateFilter({ eventId: s?.value || "" });
  const handleMarketChange = (s) => updateFilter({ market: s?.value || "" });
  const handleCurrencyChange = (s) =>
    updateFilter({ currency: s?.value || "" });

  // CustomDatePicker calls setFilter directly; this wrapper makes sure
  // a date change also goes back to page 1
  const setFilterFromPicker = (updater) =>
    setFilter((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      return { ...next, page: 1 };
    });

  // page change applies immediately (no debounce wait)
  const handlePageChange = (newPage) => {
    setFilter((prev) => ({ ...prev, page: newPage }));
    setDebouncedFilter((prev) => ({ ...prev, page: newPage }));
  };

  const applyPreset = (preset) => {
    let fromPeriod, toPeriod;

    if (preset === "today") {
      fromPeriod = moment().startOf("day");
      toPeriod = moment().endOf("day");
    } else {
      fromPeriod = moment().subtract(1, "day").startOf("day");
      toPeriod = moment().subtract(1, "day").endOf("day");
    }

    const nextFilter = {
      ...filter,
      page: 1,
      fromPeriod: fromPeriod.format("YYYY-MM-DD HH:mm:ss"),
      toPeriod: toPeriod.format("YYYY-MM-DD HH:mm:ss"),
    };

    setFilter(nextFilter);
    setDebouncedFilter(nextFilter); // apply immediately
  };

  const handleRefresh = () => {
    setDebouncedFilter(filter); // apply any pending typed values now
    setRefreshKey((k) => k + 1); // force a fetch even if nothing changed
  };

  const handleShowDefault = () => {
    const fresh = getDefaultFilter();
    setFilter(fresh);
    setDebouncedFilter(fresh);
    setRefreshKey((k) => k + 1);
  };

  const rows = Array.isArray(data) ? data : [];

  // map the API pagination keys to what the <Pagination /> component reads
  const paginationData = {
    ...pagination,
    totalDocs: pagination.totalRecords,
    hasPrevPage: pagination.hasPreviousPage,
  };

  return (
    <app-betlist _nghost-serverapp-c131 className="ng-star-inserted">
      <div
        _ngcontent-serverapp-c131
        className="full-wrap"
        style={{ height: "calc(100% - 105px)" }}
      >
        <div _ngcontent-serverapp-c131 className="over-wrap">
          <div _ngcontent-serverapp-c131 className="total_all">
            <h2 _ngcontent-serverapp-c131>Bet List</h2>
            <div _ngcontent-serverapp-c131 className="spacer" />
            <span _ngcontent-serverapp-c131 className="total-users">
              Total Records:{" "}
              <strong _ngcontent-serverapp-c131>
                {pagination.totalRecords}
              </strong>
            </span>
            &nbsp;
          </div>
          <div _ngcontent-serverapp-c131 className="function-wrap funwrap">
            <ul _ngcontent-serverapp-c131 className="input-list diaplayflex">
              <li id="eventRadioBtnList">
                {eventTypes.map(({ label, value }) => (
                  <React.Fragment key={value}>
                    <input
                      type="radio"
                      name="eventType"
                      id={`type-${value}`}
                      value={value}
                      checked={filter?.type == value}
                      onChange={(e) => handleTypeChange(e.target.value)}
                    />
                    <label htmlFor={`type-${value}`}>{label}</label>
                  </React.Fragment>
                ))}
                {/* <input
                  type="radio"
                  name="eventType"
                  id="All"
                  value="all"
                  checked={filter?.type === "all"}
                  onChange={(e) => handleTypeChange(e.target.value)}
                />
                <label htmlFor="All">All</label>
                {betCheckData.map(({ label, value }) => (
                  <React.Fragment key={value}>
                    <input
                      type="radio"
                      name="eventType" // unique group name
                      id={`type-${value}`} // unique id
                      value={value}
                      checked={filter?.type == value}
                      onChange={(e) => handleTypeChange(e.target.value)}
                    />
                    <label htmlFor={`type-${value}`}>{label}</label>
                  </React.Fragment>
                ))}

                {filter.type != 3 &&
                  betCheckDataInner.map(({ label, value }) => (
                    <React.Fragment key={value}>
                      <input
                        type="radio"
                        name="eventBetType" // different unique group name
                        id={`betType-${value}`} // unique id
                        value={value}
                        checked={filter?.betType == value}
                        onChange={(e) => handleBetTypeChange(e.target.value)}
                      />
                      <label htmlFor={`betType-${value}`}>{label}</label>
                    </React.Fragment>
                  ))} */}
              </li>
            </ul>

            <ul
              _ngcontent-serverapp-c131
              className="input-list contentclass wrapInput"
            >
              <div
                _ngcontent-serverapp-c131
                id="statusCondition"
                style={{ display: "flex" }}
              >
                <li _ngcontent-serverapp-c131>
                  <label _ngcontent-serverapp-c131>Bet Status:</label>
                </li>
                <li _ngcontent-serverapp-c131>
                  <select
                    name="betStatus"
                    id="betStatus"
                    className="ng-untouched ng-pristine ng-valid"
                    value={filter?.status ?? "completed"}
                    onChange={handleStatusChange}
                  >
                    <option className="ng-star-inserted" value="completed">
                      Settled
                    </option>
                    <option className="ng-star-inserted" value="suspend">
                      Cancelled
                    </option>
                    <option className="ng-star-inserted" value="voided">
                      Voided
                    </option>
                  </select>
                </li>
              </div>
              <li _ngcontent-serverapp-c131>
                <label _ngcontent-serverapp-c131>Period:</label>
              </li>
              <CustomDatePicker
                type="new"
                filter={filter}
                setFilter={setFilterFromPicker}
              />
              <div className="diaplayflex divblock">
                <li className="widthfull">
                  <a
                    id="today"
                    className="btn widthfull"
                    onClick={() => applyPreset("today")}
                  >
                    Just for today
                  </a>
                </li>
                <li className="widthfull">
                  <a
                    id="yesterday"
                    className="btn widthfull"
                    onClick={() => applyPreset("yesterday")}
                  >
                    From yesterday
                  </a>
                </li>
              </div>
              <li _ngcontent-serverapp-c131 style={{ display: "none" }}>
                (Time Zone:IST)
              </li>
              <div _ngcontent-serverapp-c131 className="diaplayflex divblock">
                <li
                  onClick={handleShowDefault}
                  _ngcontent-serverapp-c131
                  className="widthfull"
                >
                  <a _ngcontent-serverapp-c131 id="getPL" className="btn-send">
                    Show Default
                  </a>
                </li>
                <li _ngcontent-serverapp-c131 className="widthfull">
                  <a
                    onClick={handleRefresh}
                    _ngcontent-serverapp-c131
                    id="getPL"
                    className="btn-send"
                  >
                    Get History
                  </a>
                </li>
              </div>
              <div _ngcontent-serverapp-c131 className="diaplayflex divblock">
                <li _ngcontent-serverapp-c131>
                  <label _ngcontent-serverapp-c131>Last:</label>
                  <select
                    _ngcontent-serverapp-c131
                    name="limit"
                    id="limit"
                    value={filter?.limit}
                    onChange={handleLimitChange}
                    className="ng-untouched ng-pristine ng-valid"
                  >
                    {LIMIT_OPTIONS.map((val) => (
                      <option key={val} _ngcontent-serverapp-c131 value={val}>
                        {`${val} Txn`}
                      </option>
                    ))}
                  </select>
                </li>
                <li _ngcontent-serverapp-c131>
                  <label _ngcontent-serverapp-c131>Sort by:</label>
                  <select
                    _ngcontent-serverapp-c131
                    name="sortBy"
                    id="sortBy"
                    value={filter?.sortBy}
                    onChange={handleSortChange}
                    className="ng-untouched ng-pristine ng-valid"
                  >
                    <option _ngcontent-serverapp-c131 value={"BetSettledTime"}>
                      Bet Settled Time
                    </option>
                    <option _ngcontent-serverapp-c131 value={"BetTakenTime"}>
                      Bet Taken Time
                    </option>
                  </select>
                </li>
              </div>
            </ul>

            <ul _ngcontent-serverapp-c131 className="input-list contentclass">
              <div _ngcontent-serverapp-c131 className="diaplayflex divblock">
                <li _ngcontent-serverapp-c131>
                  <input
                    _ngcontent-serverapp-c131
                    appinputrestriction="noSpecialChars3"
                    type="text"
                    name="search"
                    id="search"
                    placeholder="Search Betid"
                    className="ng-untouched ng-pristine ng-valid"
                    value={filter?.search ?? ""}
                    onChange={handleSearchChange}
                  />
                </li>
                <li _ngcontent-serverapp-c131 className="ng-star-inserted">
                  <input
                    _ngcontent-serverapp-c131
                    type="text"
                    name="ipAddress"
                    placeholder="Search IP"
                    className="ng-untouched ng-pristine ng-valid"
                    value={filter?.ipAddress ?? ""}
                    onChange={handleIpChange}
                  />
                </li>

                <li _ngcontent-serverapp-c131>
                  <label _ngcontent-serverapp-c131> Stake </label>
                  <input
                    _ngcontent-serverapp-c131
                    type="number"
                    name="Volume Check"
                    placeholder="Volume"
                    className="ng-untouched ng-pristine ng-valid"
                    value={filter?.stake ?? ""}
                    onChange={handleStakeChange}
                  />
                </li>
              </div>
              <div _ngcontent-serverapp-c131 className="diaplayflex1 divblock">
                <li
                  _ngcontent-serverapp-c131
                  className="widthfull ng-star-inserted"
                >
                  <div className="ng-tns-c130-1 ng-star-inserted ng-untouched ng-pristine ng-valid">
                    <Select
                      options={userOptions}
                      placeholder="Select User"
                      isSearchable={false}
                      isClearable={true}
                      value={
                        userOptions.find((o) => o.value === filter?.userId) ||
                        null
                      }
                      onChange={handleUserChange}
                      className="ng-tns-c130-1 p-dropdown p-component p-dropdown-clearable"
                      classNamePrefix="p-dropdown"
                    />
                  </div>
                </li>

                <li _ngcontent-serverapp-c131></li>

                <li _ngcontent-serverapp-c131 className="widthfull">
                  <div className="widthfull ng-select ng-select-single ng-select-searchable ng-select-clearable ng-untouched ng-pristine ng-valid">
                    <Select
                      options={eventOptions}
                      placeholder="Select Event"
                      isSearchable={true}
                      isClearable={true}
                      value={
                        eventOptions.find((o) => o.value === filter?.eventId) ||
                        null
                      }
                      onChange={handleEventChange}
                      className="widthfull"
                      classNamePrefix="ng"
                    />
                  </div>
                </li>
              </div>
              <div _ngcontent-serverapp-c131 className="diaplayflex1 divblock">
                <li _ngcontent-serverapp-c131 className="widthfull">
                  <div className="widthfull ng-select ng-select-single ng-select-searchable ng-select-clearable ng-untouched ng-pristine ng-valid">
                    <Select
                      options={marketOptions}
                      placeholder="Select Market"
                      isSearchable={true}
                      isClearable={true}
                      value={
                        marketOptions.find((o) => o.value === filter?.market) ||
                        null
                      }
                      onChange={handleMarketChange}
                      className="widthfull"
                      classNamePrefix="ng"
                    />
                  </div>
                </li>

                <li _ngcontent-serverapp-c131 className="widthfull">
                  <div className="widthfull ng-select ng-select-single ng-select-searchable ng-select-clearable ng-untouched ng-pristine ng-valid">
                    <Select
                      options={currencyOptions}
                      placeholder="Select Currency"
                      isSearchable={true}
                      isClearable={true}
                      value={
                        currencyOptions.find(
                          (o) => o.value === filter?.currency,
                        ) || null
                      }
                      onChange={handleCurrencyChange}
                      className="widthfull"
                      classNamePrefix="ng"
                    />
                  </div>
                </li>
              </div>
              <li _ngcontent-serverapp-c131></li>
            </ul>
          </div>
          <div _ngcontent-serverapp-c131 className="total_all">
            <span _ngcontent-serverapp-c131 />
            <div _ngcontent-serverapp-c131 className="spacer" />
          </div>
          <div
            _ngcontent-serverapp-c131
            className="table-responsive data-table"
          >
            <div _ngcontent-serverapp-c131 id="report">
              <table
                _ngcontent-serverapp-c119
                id="table_DL"
                className="table-s"
                style={{ display: "table" }}
              >
                <tbody _ngcontent-serverapp-c119>
                  {filter?.betType == "parlay" ? (
                    <tr _ngcontent-serverapp-c84>
                      {DownlineHeader(user?.userType)?.map((res) => {
                        return (
                          <th
                            key={res?.key}
                            _ngcontent-serverapp-c84
                            width="5%"
                            data-order="asc"
                            data-name="playerId"
                            className="align-L sort_asc ng-star-inserted"
                          >
                            {" "}
                            {res?.value} ID{" "}
                          </th>
                        );
                      })}

                      <th
                        _ngcontent-serverapp-c84
                        width="5%"
                        data-order="asc"
                        data-name="playerId"
                        className="align-L sort_asc"
                      >
                        PL ID{" "}
                      </th>
                      <th
                        _ngcontent-serverapp-c84
                        width="5%"
                        data-order="asc"
                        data-name="consolidateId"
                        className="align-L sort_asc"
                      >
                        {" "}
                        Bet ID
                      </th>
                      <th
                        _ngcontent-serverapp-c84
                        id="betTime"
                        width="6%"
                        data-order="asc"
                        data-name="betTime"
                        className="align-L sort_asc"
                      >
                        Bet taken{" "}
                      </th>

                      <th
                        _ngcontent-serverapp-c84
                        width="7%"
                        data-order="asc"
                        data-name="ipAddress"
                        className="align-L sort_asc"
                      >
                        {" "}
                        IP Address{" "}
                      </th>
                      <th
                        _ngcontent-serverapp-c84
                        width
                        data-order="asc"
                        data-name="eventName"
                        className="align-L sort_asc"
                      >
                        List Bets{" "}
                      </th>
                      <th
                        _ngcontent-serverapp-c84
                        width="10%"
                        data-order="asc"
                        data-name="selName"
                        className="align-L sort_asc"
                      >
                        Event{" "}
                      </th>
                      <th
                        _ngcontent-serverapp-c84
                        width="4%"
                        data-order="asc"
                        data-name="betType"
                        className="align-C sort_asc"
                      >
                        {" "}
                        Type{" "}
                      </th>
                      <th
                        _ngcontent-serverapp-c84
                        width="7%"
                        data-order="asc"
                        data-name="odds"
                        className="sort_asc"
                      >
                        Odds req{" "}
                      </th>
                      <th
                        _ngcontent-serverapp-c84
                        width="5%"
                        data-order="asc"
                        data-name="stake"
                        className="sort_asc"
                      >
                        Stake{" "}
                      </th>
                      <th
                        _ngcontent-serverapp-c84
                        width="5%"
                        name="profitOrLossTh"
                        data-order="asc"
                        data-name="PL"
                        className="sort_asc"
                      >
                        {" "}
                        Profit/Loss
                      </th>
                      <th
                        _ngcontent-serverapp-c84
                        width="5%"
                        name="status"
                        data-order="asc"
                        data-name="status"
                        className="sort_asc"
                      >
                        {" "}
                        Status
                      </th>

                      <th
                        _ngcontent-serverapp-c84
                        width="5%"
                        name="remark"
                        data-order="asc"
                        data-name="remark"
                        className="align-C sort_asc"
                      >
                        Remark
                      </th>
                    </tr>
                  ) : (
                    <tr _ngcontent-serverapp-c119>
                      {DownlineHeader(user?.userType)?.map((res) => {
                        return (
                          <th
                            key={res?.key}
                            _ngcontent-serverapp-c84
                            width="5%"
                            data-order="asc"
                            data-name="playerId"
                            className="align-L sort_asc ng-star-inserted"
                          >
                            {" "}
                            {res?.value} ID{" "}
                          </th>
                        );
                      })}

                      <th
                        style={style}
                        _ngcontent-serverapp-c119
                        width="5%"
                        data-order="asc"
                        data-name="playerId"
                        className="align-L sort_asc"
                      >
                        PL ID
                      </th>
                      <th
                        style={style}
                        _ngcontent-serverapp-c119
                        width="5%"
                        data-order="asc"
                        data-name="consolidateId"
                        className="align-L sort_asc"
                      >
                        Bet ID
                      </th>
                      <th
                        style={style}
                        _ngcontent-serverapp-c119
                        id="betTime"
                        width="6%"
                        data-order="asc"
                        data-name="betTime"
                        className="align-L sort_asc"
                      >
                        Bet taken
                      </th>
                      <th
                        style={style}
                        _ngcontent-serverapp-c119
                        id="settlementTime"
                        width="7%"
                        data-order="asc"
                        data-name="settlementTime"
                        className="align-L sort_asc"
                      >
                        Bet Settled
                      </th>
                      <th
                        style={style}
                        _ngcontent-serverapp-c119
                        width
                        data-order="asc"
                        data-name="eventName"
                        className="align-L sort_asc"
                      >
                        Event
                      </th>
                      <th
                        style={style}
                        _ngcontent-serverapp-c119
                        width="7%"
                        data-order="asc"
                        data-name="selName"
                        className="align-L sort_asc"
                      >
                        Market
                      </th>
                      <th
                        style={style}
                        _ngcontent-serverapp-c119
                        width="4%"
                        data-order="asc"
                        data-name="betType"
                        className="align-C sort_asc"
                      >
                        Type
                      </th>
                      <th
                        style={style}
                        _ngcontent-serverapp-c119
                        width="7%"
                        data-order="asc"
                        data-name="odds"
                        className="sort_asc"
                      >
                        Odds req.
                      </th>
                      <th
                        style={style}
                        _ngcontent-serverapp-c119
                        width="5%"
                        data-order="asc"
                        data-name="stake"
                        className="sort_asc"
                      >
                        Stake
                      </th>
                      <th
                        style={style}
                        _ngcontent-serverapp-c119
                        width="5%"
                        name="profitOrLossTh"
                        data-order="asc"
                        data-name="PL"
                        className="sort_asc"
                      >
                        Profit/Loss
                      </th>
                      <th
                        style={style}
                        _ngcontent-serverapp-c119
                        width="5%"
                        name="status"
                        data-order="asc"
                        data-name="status"
                        className="sort_asc"
                      >
                        Status
                      </th>

                      <th
                        style={style}
                        _ngcontent-serverapp-c119
                        width="5%"
                        name="remark"
                        data-order="asc"
                        data-name="remark"
                        className="align-C sort_asc"
                      >
                        Remark
                      </th>
                      <th
                        style={style}
                        _ngcontent-serverapp-c119
                        width="7%"
                        data-order="asc"
                        data-name="ipAddress"
                        className="align-L sort_asc"
                      >
                        IP Address
                      </th>
                    </tr>
                  )}
                  {rows.length > 0 ? (
                    filter?.betType == "parlay" ? (
                      <ParlayBets
                        filter={filter}
                        data={rows}
                        show={show}
                        setShow={setShow}
                      />
                    ) : (
                      rows.map((item, index) => {
                        return (
                          <tr
                            key={
                              item?._id ||
                              item?.matchBetId ||
                              item?.casinoBetId ||
                              index
                            }
                            _ngcontent-serverapp-c119
                            id="matchRow0"
                            className="ng-star-inserted"
                          >
                            {DownlineHeader(user?.userType)?.map((res) => {
                              return (
                                <td
                                  key={res?.key}
                                  style={styleBody}
                                  _ngcontent-serverapp-c119
                                  id="agentUserId2"
                                  className="align-L ng-star-inserted"
                                >
                                  {item[res?.key]?.username || "N/A"}
                                </td>
                              );
                            })}

                            <td
                              style={styleBody}
                              _ngcontent-serverapp-c119
                              id="playerId"
                              className="align-L"
                            >
                              {item?.clientName || "-"}
                            </td>
                            <td
                              style={styleBody}
                              _ngcontent-serverapp-c119
                              className="align-L"
                            >
                              <a href="#" _ngcontent-serverapp-c119 id="betID">
                                {item?.matchBetId ||
                                  item?.casinoBetId ||
                                  item?.sessionBetId}
                              </a>
                            </td>
                            <td
                              style={styleBody}
                              _ngcontent-serverapp-c119
                              className="align-L ng-star-inserted"
                            >
                              <span
                                _ngcontent-serverapp-c119
                                id="betPlaced"
                                className="small-date"
                              >
                                {formatDate(
                                  item?.timeInserted ||
                                    item?.createdAt ||
                                    item?.betPlaced,
                                )}
                              </span>
                            </td>

                            <td
                              style={styleBody}
                              _ngcontent-serverapp-c119
                              className="align-L ng-star-inserted"
                            >
                              <span
                                _ngcontent-serverapp-c119
                                id="betsettled"
                                className="small-date"
                              >
                                {formatDate(item?.updatedAt)}
                              </span>
                            </td>

                            <td
                              style={styleBody}
                              _ngcontent-serverapp-c119
                              id="matchTitle"
                              className="align-L"
                              title="cricket > Tiruppur Tamizhans v Salem Spartans > 6 Over SS"
                            >
                              {item?.sportName ||
                                betCheckObj[item?.eventType] ||
                                item?.platform}
                              <img
                                _ngcontent-serverapp-c119
                                src="/assets/images/transparent.gif"
                                className="fromto"
                              />
                              {item?.casinoName || item?.matchName}
                              <img
                                _ngcontent-serverapp-c119
                                src="/assets/images/transparent.gif"
                                className="fromto"
                              />
                              <strong _ngcontent-serverapp-c119>
                                {item?.exchangeType == "session"
                                  ? "Fancy Bet"
                                  : item?.exchangeType == "bookmaker"
                                    ? "Bookmaker"
                                    : item?.market ||
                                      item?.fancyName ||
                                      item?.gameCode}{" "}
                              </strong>
                            </td>
                            <td
                              style={styleBody}
                              _ngcontent-serverapp-c119
                              id="matchSelection"
                              className="align-L"
                            >
                              {item?.exchangeType == "session"
                                ? item?.fancyName
                                : item?.runnerName ||
                                  item?.selectionName ||
                                  item?.teamName ||
                                  item?.fancyName ||
                                  item?.platform}
                            </td>
                            <td
                              style={styleBody}
                              _ngcontent-serverapp-c119
                              className="align-C"
                            >
                              <span
                                _ngcontent-serverapp-c119
                                id="matchType"
                                className={
                                  item?.betType === "back" ||
                                  item?.type === "Yes"
                                    ? "yes"
                                    : "no"
                                }
                              >
                                {startCase(
                                  item?.betType || item?.type || item?.gameCode,
                                )}{" "}
                              </span>
                            </td>
                            <td
                              style={styleBody}
                              _ngcontent-serverapp-c119
                              id="matchOddsReq"
                            >
                              {item?.exchangeType == "session"
                                ? item?.betRun + ":" + item?.bhav
                                : toFixedWithoutRounding(item?.bhav) ||
                                  "-"}{" "}
                            </td>
                            <td
                              style={styleBody}
                              _ngcontent-serverapp-c119
                              id="matchStake"
                            >
                              {item?.amount || item?.betAmount}{" "}
                              <strong
                                _ngcontent-serverapp-c119
                                className="ng-star-inserted"
                              >
                                (USD)
                              </strong>
                            </td>
                            <ProfitLossCell
                              item={item}
                              filter={filter}
                              getBetType={item?.exchangeType}
                              styleBody={styleBody}
                            />
                            <td style={styleBody} _ngcontent-serverapp-c119>
                              <span
                                _ngcontent-serverapp-c119
                                id="pol"
                                className="small-date"
                              >
                                Settled
                              </span>
                            </td>

                            <td
                              style={styleBody}
                              _ngcontent-serverapp-c119
                              id="remark"
                              className="align-"
                            ></td>
                            <td
                              style={styleBody}
                              _ngcontent-serverapp-c119
                              id="ip"
                              className="align-L"
                            >
                              {(item?.ipAddress &&
                                item?.ipAddress.replace("::ffff:", "")) ||
                                "-"}
                            </td>
                          </tr>
                        );
                      })
                    )
                  ) : (
                    <tr>
                      <td colSpan={13}>
                        <span>You have no bets in this time period.</span>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              <p
                _ngcontent-serverapp-c119=""
                className="table-other"
                style={{ marginTop: "5px" }}
              >
                Bet List is shown net of commission.
              </p>
            </div>
          </div>{" "}
          {rows.length > 0 && pagination.totalRecords > 0 && (
            <Pagination
              pagination={paginationData}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      </div>

      <ParlayModal isOpen={show?.status} data={show?.data} onClose={setShow} />
    </app-betlist>
  );
};

export default BetList;
const eventTypes = [
  { value: "all", label: "All" },
  { value: "soccer", label: "Soccer" },
  { value: "tennis", label: "Tennis" },
  { value: "cricket", label: "Cricket" },
  { value: "sportbook", label: "Premium" },
  { value: "bookmaker", label: "Bookmaker" },
  { value: "fancy", label: "Fancy" },
  // { value: "parlay", label: "parlay" },
  // { value: "Live Casino", label: "Live Casino" },
  { value: "horseracing", label: "Horse Racing" },
  { value: "greyhound", label: "Greyhound Racing" },
  { value: "kabaddi", label: "Kabaddi" },
  { value: "election", label: "Election" },
];
